import React, { useContext } from 'react'
import { FormikValues, useFormikContext } from 'formik'
import SubmitButton from '../../shared/SubmitButton'
import { LocalizationContext } from '../../shared/contexts'
import { formatTenantDateTime } from '../../../utils/formatTenantDateTime'
import { TIME_SOURCE } from '../../../utils/timeSource'
import { t } from '../../../i18n'
import { useCatalogue } from '../CatalogueProvider'
import { CatalogueItem, ItemKind, ProductRow } from '../catalogueTypes'

const scope = 'product'

const TYPE_OPTIONS: ReadonlyArray<{ label: string; value: ItemKind }> = [
  { label: t('category'), value: 'category' },
  { label: t('product', { scope }), value: 'product' }
]

export const itemTypeLabel = (type: ItemKind): string => TYPE_OPTIONS.find((option) => option.value === type)?.label ?? ''

export const itemTypeActionLabel = (action: 'create' | 'edit' | 'delete', type: ItemKind): string =>
  t(`catalogue.${action}`, { type: itemTypeLabel(type).toLowerCase() })

interface CatalogueItemTypeOptionsProps {
  value: ItemKind
  disabled: boolean
  onSelect: (type: ItemKind) => void
  showDivider: boolean
}

const CatalogueItemTypeOptions = ({ value, disabled, onSelect, showDivider }: CatalogueItemTypeOptionsProps) => (
  <>
    <div className="option-radio">
      {TYPE_OPTIONS.map((option, index) => (
        <div onClick={() => onSelect(option.value)} key={option.value} className={`d-inline ${disabled ? 'option-disabled' : ''}`}>
          <input
            onChange={() => onSelect(option.value)}
            className={[index === 0 ? '' : 'margin-left-20', disabled ? 'cursor-default' : ''].join(' ')}
            type="radio"
            name={option.value}
            checked={value === option.value}
            disabled={disabled}
          />
          <label htmlFor={option.value}>{option.label}</label>
        </div>
      ))}
    </div>
    {showDivider ? <hr className="option-radio-divider" /> : null}
  </>
)

export const CatalogueItemTypeSwitch = ({ value }: { value: ItemKind }) => {
  const { meta } = useCatalogue()
  return <CatalogueItemTypeOptions value={value} disabled={false} onSelect={meta.go.newItem} showDivider={true} />
}

const noop = () => undefined

export const CatalogueItemTypeIndicator = ({ value, showDivider = true }: { value: ItemKind; showDivider?: boolean }) => (
  <CatalogueItemTypeOptions value={value} disabled={true} onSelect={noop} showDivider={showDivider} />
)

type Accent = 'blue' | 'green' | 'orange' | 'purple'

export const CatalogueFormSection = ({ title, accent, children }: { title: string; accent: Accent; children: React.ReactNode }) => (
  <div className={`form-section form-section-${accent}`}>
    <h5 className="form-section-title">{title}</h5>
    {children}
  </div>
)

interface InforSyncBadgeProps {
  product: ProductRow
  inforExternalId: string | null
  isModifier: boolean
}

export const InforSyncBadge = ({ product, inforExternalId, isModifier }: InforSyncBadgeProps) => {
  const localization = useContext(LocalizationContext)

  if (!inforExternalId || !isModifier) return null

  if (product.infor_last_synced_at === null) return <span className="badge bg-danger me-3">{t('catalogue.infor_not_synced')}</span>

  const syncedAt = formatTenantDateTime(product.infor_last_synced_at, localization)
  const stale = new Date(product.infor_last_synced_at) < new Date(product.updated_at)

  return (
    <span className={`badge me-3 ${stale ? 'bg-warning text-dark' : 'bg-success'}`} title={TIME_SOURCE.app}>
      {t('catalogue.last_synced_at', { time: syncedAt })}
    </span>
  )
}

export const ProductInforSyncBadge = ({ product }: { product: ProductRow }) => {
  const { values } = useFormikContext<{ infor_external_id: string; is_modifier: boolean }>()
  return <InforSyncBadge product={product} inforExternalId={values.infor_external_id} isModifier={values.is_modifier} />
}

export const CatalogueSubmitButton = ({ children }: { children: React.ReactNode }) => {
  const { submitForm } = useFormikContext<FormikValues>()
  return (
    <SubmitButton className="btn btn-primary" onClick={() => submitForm()}>
      {children}
    </SubmitButton>
  )
}

export const CatalogueCancelButton = () => {
  const { meta } = useCatalogue()
  return (
    <button type="button" className="btn btn-secondary me-3" onClick={meta.go.browse}>
      {t('cancel')}
    </button>
  )
}

export const CatalogueDeleteButton = ({ item, children }: { item: CatalogueItem; children: React.ReactNode }) => {
  const { actions } = useCatalogue()
  return (
    <button type="button" className="btn btn-danger me-3" onClick={() => actions.requestDelete(item)}>
      {children}
    </button>
  )
}
